/**
 * Backside Finish Handler
 *
 * Hides the backside finishes a thermofoil variant cannot be made with, in the
 * OPC (SE Option Price Calculator) "Backside Finish" dropdown.
 *
 * Availability is authored per variant on the custom.thermofoil_backside_finish
 * metaobject and emitted as JSON by snippets/backside-finish-data.liquid. The
 * merchant ticks six booleans; four options are ever visible, because each of
 * Matching and Close Match exists twice — once paid, once included free — and
 * the customer must never be offered the same finish at two prices.
 *
 * Product gating happens in Liquid: sections/main-product.liquid renders the
 * payload and this script only for products tagged RTF.
 *
 * It fails OPEN. A variant with no metaobject authored yet filters nothing, so
 * the dropdown behaves exactly as it does today while the catalog is filled in.
 * "Unconfigured" and "no finish available" are different states and are kept
 * apart all the way through: resolveFlags returns null for the first and flags
 * for the second.
 */

(function () {
  'use strict';

  /**
   * The option text OPC renders, exactly.
   *
   * This is the coupling point with the app, and the only thing that can be
   * wrong on launch — the options are authored in OPC's admin, not here. If a
   * label is renamed there, change it here and nowhere else; the tests assert
   * against this object rather than against literals.
   *
   * Note the shape of each pair: the free option's text is a strict PREFIX of
   * the paid one, which carries the price on the end. Matching below is exact
   * equality for that reason. Loosening it to a substring test — as
   * rta-door-config.js does for door styles — would make "Matching Finish"
   * match the paid option too, and a variant offering the finish free would
   * leave the $3 option visible beside it.
   */
  var LABELS = {
    white: 'White',
    black: 'Black',
    matching: 'Matching Finish + $3 Per Door Sqft',
    matchingIncluded: 'Matching Finish',
    closeMatch: 'Close Matching Finish + $3 Per Door Sqft',
    closeMatchIncluded: 'Close Matching Finish'
  };

  /**
   * This variant's flags, or null when the merchant has not authored it.
   *
   * Null is not an empty flags object: empty means every finish is unavailable
   * and the dropdown should be emptied, null means we know nothing and must
   * leave the dropdown alone.
   */
  function resolveFlags(data, variantId) {
    if (!data || !data.variants || variantId === null || variantId === undefined) return null;
    var flags = data.variants[String(variantId)];
    return flags || null;
  }

  /**
   * Which option labels should stay visible, in dropdown order.
   *
   * White and Black are plain availability. Matching and Close Match each
   * resolve a paid/included pair down to a single option: whenever the included
   * flag is set the finish is free, so the paid option is suppressed even if
   * its own flag is also set. A variant that is always free therefore needs
   * only the included flag ticked.
   */
  function allowedLabels(flags) {
    if (!flags) return [];
    var out = [];

    if (flags.white) out.push(LABELS.white);
    if (flags.black) out.push(LABELS.black);

    if (flags.matchingIncluded) {
      out.push(LABELS.matchingIncluded);
    } else if (flags.matching) {
      out.push(LABELS.matching);
    }

    if (flags.closeMatchIncluded) {
      out.push(LABELS.closeMatchIncluded);
    } else if (flags.closeMatch) {
      out.push(LABELS.closeMatch);
    }

    return out;
  }

  /**
   * Compare labels on their words, not their spacing.
   *
   * OPC renders these labels with trailing whitespace, and its admin will
   * accept a double space or an nbsp inside one — any of which breaks an exact
   * match silently, leaving an option visible on a variant that cannot have it.
   * \s covers U+00A0, so collapsing runs handles all three.
   */
  function normalize(text) {
    return String(text === null || text === undefined ? '' : text)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function isAllowed(text, allowed) {
    var t = normalize(text);
    return (allowed || []).some(function (label) {
      return normalize(label) === t;
    });
  }

  /**
   * True when what the customer has already picked is no longer on offer.
   * Blank and the placeholder are not stale — nothing is selected to clear.
   */
  function needsReset(selectedLabel, allowed) {
    if (isPlaceholderLabel(selectedLabel)) return false;
    return !isAllowed(selectedLabel, allowed);
  }

  /**
   * True when an option row is OPC's placeholder rather than a real finish.
   *
   * On THIS dropdown the placeholder row carries no .dropdown-label at all, so
   * its text reads as empty — the app injects the "-- Select --" wording as
   * chrome rather than as an option. OPC's other dropdowns (Glass Ready Frame)
   * do render the text, so both shapes are accepted here.
   *
   * Keying on the "-- Select --" string alone was the bug found on 2026-09-07:
   * resetSelection never matched the unlabelled row, returned silently, and
   * left a stale finish priced on a variant that no longer offered it.
   */
  function isPlaceholderLabel(text) {
    var t = normalize(text);
    if (!t) return true;
    return t.replace(/[^a-z]/g, '') === 'select';
  }

  /**
   * True when the whole dropdown should come off the page.
   *
   * Every flag false is a positive statement, not an absence: the door is MDF,
   * which has no backside to finish. Leaving an emptied dropdown on screen
   * would ask the customer to choose from nothing.
   *
   * This is the payoff of keeping "unconfigured" and "nothing available" as
   * different states. Null flags return false here — a variant the merchant has
   * not authored is not MDF, and hiding on null would blank the control across
   * every unfilled variant in the catalog.
   */
  function shouldHideOption(flags) {
    if (!flags) return false;
    return allowedLabels(flags).length === 0;
  }

  /**
   * Which variant the cart "Edit Options" modal currently describes.
   *
   * The modal is OPC's own and carries no variant id — only native selects
   * named after each Shopify option ("Color"), holding a plain option value
   * ("Beach Glass Supermatte"). Recover the id by matching those against each
   * candidate variant's own options, in the product's declared option order.
   *
   * `selected` is a plain {optionName: value} map read from the modal's DOM;
   * kept as a parameter so this stays testable with no DOM. An option name the
   * modal does not expose (OPC adds Glass Ready Frame, Finish, etc., which are
   * not Shopify options) cannot be used to rule a candidate out.
   *
   * Same technique as variant-dimensions-handler.js's variantFromModal, for the
   * same modal. Copied rather than shared: the two features would otherwise be
   * coupled through a file neither owns, for a function this small.
   */
  function variantFromModal(entry, selected) {
    if (!entry || !entry.optionNames || !entry.variants || !selected) return null;

    for (var variantId in entry.variants) {
      if (!Object.prototype.hasOwnProperty.call(entry.variants, variantId)) continue;
      var options = entry.variants[variantId].options;
      if (!options) continue;

      var matches = true;
      for (var i = 0; i < entry.optionNames.length; i++) {
        var optionName = entry.optionNames[i];
        if (!(optionName in selected)) continue;
        if (selected[optionName] !== options[i]) {
          matches = false;
          break;
        }
      }
      if (matches) return variantId;
    }

    return null;
  }

  // Under node (tests), export the pure logic and skip all DOM wiring.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      LABELS: LABELS,
      resolveFlags: resolveFlags,
      allowedLabels: allowedLabels,
      needsReset: needsReset,
      shouldHideOption: shouldHideOption,
      isPlaceholderLabel: isPlaceholderLabel,
      variantFromModal: variantFromModal
    };
    return;
  }

  var DROPDOWN_SELECTOR = '.thermofoil-backside-finish-options';
  var OPTION_SELECTOR = '.tab-view';
  var LABEL_SELECTOR = '.dropdown-label';
  var INPUT_SELECTOR = 'input';
  var REAPPLY_DEBOUNCE_MS = 50;

  // Product page payload: one product, rendered by backside-finish-data.liquid.
  var payloadScript = document.querySelector('[id^="BacksideFinish-"]');

  // Cart payload: every RTF product in the cart, keyed by product id, rendered
  // by cart-backside-finish-data.liquid. The cart page and the cart drawer each
  // render one, so this is read as a list and merged.
  var hasCartData = document.querySelector('[data-cart-backside-finish]') !== null;

  if (!payloadScript && !hasCartData) return;

  function parseJsonScript(el, label) {
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Failed to parse ' + label + ':', e);
      return null;
    }
  }

  var payload = payloadScript ? parseJsonScript(payloadScript, 'backside finish data') : null;

  function readCartData() {
    var merged = {};
    var scripts = document.querySelectorAll('[data-cart-backside-finish]');
    for (var i = 0; i < scripts.length; i++) {
      var parsed = parseJsonScript(scripts[i], 'cart backside finish data');
      if (!parsed) continue;
      for (var key in parsed) {
        if (Object.prototype.hasOwnProperty.call(parsed, key)) merged[key] = parsed[key];
      }
    }
    return merged;
  }

  var observer = null;
  var observedRoot = null;
  var lastFlags = null;
  var lastVariantId = null;
  var productResetGuard = makeResetGuard();

  // Bounds reset attempts per variant change. Clearing the dropdown makes OPC
  // re-render, which re-enters the observer — that settles on its own, because
  // a cleared control reads as the placeholder and is no longer stale. This cap
  // exists for the case that does NOT settle: OPC restoring the stale value
  // every time we clear it. Without it that is a hot loop clicking the app's
  // own control, which re-prices on every click.
  var MAX_RESET_ATTEMPTS = 3;

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  function dropdown() {
    return document.querySelector(DROPDOWN_SELECTOR);
  }

  /**
   * What the dropdown currently holds. OPC keeps the chosen value on a hidden
   * input inside the dropdown container, which survives its menu re-renders —
   * the visible label does not.
   */
  function currentSelection(root) {
    var input = root.querySelector(INPUT_SELECTOR);
    return input ? input.value : '';
  }

  /**
   * Is this row OPC's placeholder rather than a real finish?
   *
   * OPC stamps the row id "<option-set-id>-DEFAULT" and puts the wording in a
   * title attribute and a bare <label> — NOT in the .dropdown-label every real
   * option uses. Checking the id first keys on the app's own marker; the title
   * and the empty-label fallback cover its other dropdowns, which render the
   * text conventionally.
   */
  function isPlaceholderRow(option) {
    if (/-DEFAULT$/i.test(option.getAttribute('id') || '')) return true;
    var title = option.getAttribute('title');
    if (title && isPlaceholderLabel(title)) return true;
    return isPlaceholderLabel(optionLabel(option));
  }

  function optionLabel(option) {
    var label = option.querySelector(LABEL_SELECTOR);
    return label ? label.textContent.trim() : '';
  }

  /**
   * Hide every option not in `allowed`, leaving the placeholder alone so the
   * customer always has something to fall back to.
   */
  function applyFilter(root, allowed) {
    var options = root.querySelectorAll(OPTION_SELECTOR);
    for (var i = 0; i < options.length; i++) {
      if (isPlaceholderRow(options[i])) continue;
      var text = optionLabel(options[i]);
      if (!text) continue;

      options[i].style.display = isAllowed(text, allowed) ? '' : 'none';
    }
  }

  /**
   * Put the dropdown back to its placeholder.
   *
   * OPC may listen on the press events as well as click, so dispatch
   * mousedown/mouseup and then exactly ONE click — never both .click() and a
   * dispatched 'click', or the app activates twice and re-prices twice. Same
   * rule and reason as door-dimension-confirm.js reClick(); rta-door-config.js
   * predates it and does send both.
   *
   * Then fire change/input on the value input so OPC recalculates.
   */
  function resetSelection(root) {
    var options = root.querySelectorAll(OPTION_SELECTOR);
    var placeholder = null;
    for (var i = 0; i < options.length; i++) {
      if (isPlaceholderRow(options[i])) {
        placeholder = options[i];
        break;
      }
    }
    if (!placeholder) return;

    var input = root.querySelector(INPUT_SELECTOR);
    ['mousedown', 'mouseup'].forEach(function (type) {
      placeholder.dispatchEvent(new MouseEvent(type, {
        view: window,
        bubbles: true,
        cancelable: true
      }));
    });
    placeholder.click();
    if (input) {
      ['change', 'input'].forEach(function (type) {
        input.dispatchEvent(new Event(type, { bubbles: true }));
      });
    }
  }

  /**
   * Undo everything a previous variant's flags may have done — both the
   * per-option filter and, if that variant was MDF, the hidden container.
   * Used whenever the new variant has no flags of its own: an unconfigured
   * variant shows whatever OPC shows, not whatever the last variant left
   * behind. Restoring the options but not the container would leave the
   * dropdown invisible on an unconfigured variant reached from an MDF one.
   */
  function clearFilter(root) {
    root.style.display = '';
    var options = root.querySelectorAll(OPTION_SELECTOR);
    for (var i = 0; i < options.length; i++) options[i].style.display = '';
  }

  /**
   * A reset-attempt budget plus the reset itself, as one unit so two contexts
   * (product page, cart modal) can be live on the same page — a product page
   * open behind an opened cart drawer — without spending or starving each
   * other's attempts.
   */
  function makeResetGuard() {
    var attempts = 0;
    return {
      reset: function () {
        attempts = 0;
      },
      /**
       * Clear the control if what is selected is no longer on offer.
       *
       * Called on a variant change, and again from the product page's menu
       * observer: OPC can finish re-rendering AFTER the change has already
       * been handled, and a re-render that restores a stale selection would
       * otherwise sit there priced.
       */
      applyIfStale: function (root, allowed) {
        if (attempts >= MAX_RESET_ATTEMPTS) return;
        if (!needsReset(currentSelection(root), allowed)) return;
        attempts++;

        // On an MDF variant the container is already hidden, and OPC does not
        // register a click inside display:none — so unhide for the duration
        // of the click and put it back. This is the case where a stale pick
        // matters most: a backside finish priced on a door that has no
        // backside. Both writes happen in one task, so nothing is painted in
        // between.
        var hidden = root.style.display === 'none';
        if (hidden) root.style.display = '';
        resetSelection(root);
        if (hidden) root.style.display = 'none';
      }
    };
  }

  /**
   * OPC rebuilds the menu's children whenever it re-renders, wiping our
   * display:none. Re-apply against the last known allowed set rather than
   * re-deriving it, since the variant has not changed.
   */
  var reapply = debounce(function () {
    var current = dropdown();
    if (!current || !lastFlags) return;
    applyVerdict(current, lastFlags, productResetGuard);
  }, REAPPLY_DEBOUNCE_MS);

  /**
   * Watch the dropdown CONTAINER, not the menu inside it.
   *
   * OPC rebuilds the menu on re-render, wiping the display:none this file set.
   * Observing the menu itself means that when OPC replaces the menu node
   * outright the observer is left attached to a detached node and can never
   * fire again to re-arm itself. The container persists, so watching it
   * survives both kinds of re-render.
   *
   * childList/subtree only, deliberately: this file's own writes are all
   * attribute changes (style.display), and without `attributes: true` they do
   * not re-enter the observer. That is what keeps applyVerdict from looping.
   */
  function observeMenu(root) {
    if (typeof MutationObserver === 'undefined') return;
    if (root === observedRoot) return;

    if (observer) observer.disconnect();
    observedRoot = root;
    observer = new MutationObserver(reapply);
    observer.observe(root, { childList: true, subtree: true });
  }

  /**
   * Put the dropdown into the state `flags` describes: filtered, cleared if the
   * current pick is gone, and hidden entirely on MDF.
   *
   * Order matters. resetSelection clicks the placeholder and OPC does not
   * register a click inside a display:none container, so the clear has to
   * happen before the MDF hide — otherwise a stale finish rides along to the
   * cart on a door that has no backside at all.
   */
  function applyVerdict(root, flags, resetGuard) {
    var allowed = allowedLabels(flags);
    applyFilter(root, allowed);
    resetGuard.applyIfStale(root, allowed);
    root.style.display = shouldHideOption(flags) ? 'none' : '';
  }

  function update(variantId) {
    var root = dropdown();
    if (!root) return false;

    // The theme's picker fires variantChanged AND writes [name="id"], so both
    // listeners below run for a single change. Without this the second pass
    // re-arms the reset budget the first may already have spent, and every
    // reset click makes OPC re-price.
    var id = variantId === null || variantId === undefined ? '' : String(variantId);
    if (id === lastVariantId) return true;
    lastVariantId = id;

    var flags = resolveFlags(payload, variantId);
    // Unconfigured variant: show whatever OPC shows, and stop filtering rather
    // than leaving the previous variant's filter in place.
    if (!flags) {
      lastFlags = null;
      if (observer) observer.disconnect();
      observedRoot = null;
      clearFilter(root);
      return true;
    }

    lastFlags = flags;
    productResetGuard.reset();
    applyVerdict(root, flags, productResetGuard);
    observeMenu(root);
    return true;
  }

  // ---- Product page: variant changes ----
  //
  // Two listeners, because two things drive the variant. The theme's own picker
  // fires variantChanged on .product-form; OPC drives its dropdowns itself and
  // writes [name="id"] directly, firing only a plain change on it. Delegated,
  // so it survives the app re-rendering the input.
  //
  // Gated on `payload`: a cart-only page (no product on it at all) has no
  // .product-form and nothing for this section to do.
  if (payload) {
    var $productForm = jQuery('.product-form');
    if ($productForm.length) {
      $productForm.on('variantChanged', function (evt, variant) {
        if (variant && variant.id) update(variant.id);
      });

      $productForm.on('change', '[name="id"]', function () {
        update(jQuery(this).val());
      });

      // The dropdown is OPC's, so it does not exist at DOMContentLoaded. Wait
      // for it, then run once for whatever variant is already selected — which
      // on a select_first_variant:false template is legitimately empty, and
      // resolves to null flags, so nothing is filtered until the customer
      // picks.
      var tryInitialPass = function () {
        return update($productForm.find('[name="id"]').val());
      };

      // Waiting for OPC to mount its dropdown. This has to watch document.body
      // — the dropdown's own container is what we are waiting for, so there is
      // no narrower node to attach to yet. That makes it the one observer here
      // that cannot be scoped, so it is bounded in the other two directions
      // instead: debounced, and disconnected after BOOT_TIMEOUT_MS.
      //
      // Without the timeout it never disconnects when OPC is slow, blocked,
      // uninstalled, or simply renders no Backside Finish option on this
      // product — leaving a whole-document subtree observer alive for the
      // page's lifetime, waking on every unrelated mutation. See
      // custom-color-opc-buttons.js for why this file avoids document.body
      // everywhere else.
      var BOOT_TIMEOUT_MS = 15000;

      if (!tryInitialPass() && typeof MutationObserver !== 'undefined') {
        var boot = new MutationObserver(debounce(function () {
          if (tryInitialPass()) boot.disconnect();
        }, REAPPLY_DEBOUNCE_MS));
        boot.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () {
          boot.disconnect();
        }, BOOT_TIMEOUT_MS);
      }
    }
  }

  // ---- Cart "Edit Options" modal ----
  //
  // The modal is OPC's own and renders the same Backside Finish dropdown as the
  // product page, opened against whichever line item's Edit Options was
  // clicked. It carries no variant id of its own — see variantFromModal above.
  //
  // dropdown() and applyVerdict() are shared with the product page section
  // unchanged: same control, same rules for what to show and when to clear a
  // stale pick. Only recovering the variant differs, because this context has
  // no [name="id"] to read.
  if (hasCartData) {
    var pending = null;
    // Own reset budget, separate from the product page's: a customer can have
    // a product page open behind an opened cart drawer, and the two contexts
    // should not spend or starve each other's attempts.
    var cartResetGuard = makeResetGuard();

    function selectedOptionsInModal() {
      var selected = {};
      var dropdowns = document.querySelectorAll('select[id^="myDropdown-"]');
      for (var i = 0; i < dropdowns.length; i++) {
        var name = dropdowns[i].name || dropdowns[i].id.slice('myDropdown-'.length);
        selected[name] = dropdowns[i].value;
      }
      return selected;
    }

    /**
     * flags is null exactly as often here as on the product page — the
     * catalog is filled in gradually, and a customer can switch the modal's
     * Color to any variant, authored or not. Without the same clearFilter
     * fallback update() uses, an unconfigured variant reached from a filtered
     * or MDF one would keep showing the PREVIOUS variant's state.
     */
    function applyToModal(entry, variantId) {
      var root = dropdown();
      if (!root) return;
      var flags = resolveFlags(entry, variantId);
      if (!flags) {
        clearFilter(root);
        return;
      }
      applyVerdict(root, flags, cartResetGuard);
    }

    // Switching Color inside the modal changes the variant. The product page
    // carries this through [name="id"]; the modal has no such input, so
    // re-derive it here. Without this the modal stays frozen on whatever the
    // line item was originally bought as.
    document.addEventListener('change', function (evt) {
      if (!pending) return;
      var target = evt.target;
      if (!target || target.tagName !== 'SELECT') return;
      if (target.id.indexOf('myDropdown-') !== 0) return;

      var entry = readCartData()[pending.productId];
      var variantId = variantFromModal(entry, selectedOptionsInModal());
      if (!variantId) return;

      pending.variantId = variantId;
      cartResetGuard.reset();
      applyToModal(entry, pending.variantId);
    });

    // Capture phase, to beat OPC's own handler to it.
    document.addEventListener('click', function (evt) {
      var target = evt.target;
      if (!target || typeof target.closest !== 'function') return;
      var item = target.closest('.cart-item[data-bf-product]');
      if (!item) return;
      pending = {
        productId: item.getAttribute('data-bf-product'),
        variantId: item.getAttribute('data-bf-variant')
      };
      cartResetGuard.reset();
    }, true);

    // Stays connected for the life of the page: the modal opens and closes
    // repeatedly, and each line item needs this re-run against its own flags.
    var cartObserver = new MutationObserver(function () {
      if (!pending) return;

      var root = dropdown();
      if (!root) return;

      var entry = readCartData()[pending.productId];
      if (!entry) return;

      // The modal's own dropdowns are the better source once it has rendered —
      // they reflect what is on screen. Fall back to the line item's variant
      // while they are still mounting.
      pending.variantId = variantFromModal(entry, selectedOptionsInModal()) || pending.variantId;

      // Applying rewrites style.display, which mutates the tree and re-enters
      // this observer. Stamp the root so one modal render is only ever handled
      // once; a re-rendered modal brings a fresh node with no mark.
      var stamp = pending.productId + ':' + pending.variantId;
      if (root.dataset.bfApplied === stamp) return;
      root.dataset.bfApplied = stamp;

      applyToModal(entry, pending.variantId);
    });

    cartObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
